import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';

export function useSeoMeta() {
  const route = useRoute();
  const { t } = useI18n();

  const updateMetaTags = () => {
    const meta = route.meta;

    if (meta) {
      const baseUrl = 'https://senyum-dental-clinic.com'; // Replace with your actual domain
      const currentUrl = `${baseUrl}${route.path}`;

      // Generate hreflang URLs for different languages
      const hreflangLinks = [
        { href: `${baseUrl}${route.path}?lang=en`, hreflang: 'en' },
        { href: `${baseUrl}${route.path}?lang=ms`, hreflang: 'ms' },
        { href: `${baseUrl}${route.path}?lang=zh`, hreflang: 'zh' },
        { href: `${baseUrl}${route.path}`, hreflang: 'x-default' }
      ];

      useHead({
        title: meta.title,
        meta: [
          // Basic meta tags
          { name: 'description', content: meta.description },
          { name: 'keywords', content: meta.keywords },

          // Open Graph tags
          { property: 'og:title', content: meta.ogTitle },
          { property: 'og:description', content: meta.ogDescription },
          { property: 'og:image', content: `${baseUrl}${meta.ogImage}` },
          { property: 'og:url', content: currentUrl },
          { property: 'og:type', content: meta.ogType },
          { property: 'og:site_name', content: 'Senyum Dental Clinic' },

          // Twitter Card tags
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: meta.ogTitle },
          { name: 'twitter:description', content: meta.ogDescription },
          { name: 'twitter:image', content: `${baseUrl}${meta.ogImage}` },

          // Additional SEO meta tags
          { name: 'robots', content: 'index, follow' },
          { name: 'author', content: 'Senyum Dental Clinic' },
          { name: 'language', content: 'en-US' },

          // Viewport and mobile optimization
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },

          // Theme and app meta tags
          { name: 'theme-color', content: '#10b981' }, // Mint green from your design
          { name: 'apple-mobile-web-app-capable', content: 'yes' },
          { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
          { name: 'apple-mobile-web-app-title', content: 'Senyum Dental' },
        ],
        link: [
          // Canonical URL
          { rel: 'canonical', href: currentUrl },

          // Hreflang tags for internationalization
          ...hreflangLinks.map(link => ({
            rel: 'alternate',
            hreflang: link.hreflang,
            href: link.href
          })),

          // Preconnect to external domains for performance
          { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
          { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
        ],
      });
    }
  };

  // Update meta tags when route changes
  watch(
    () => route.path,
    () => {
      updateMetaTags();
    },
    { immediate: true }
  );

  return {
    updateMetaTags,
  };
}
